import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

export default function Login() {
  const { user, platformRole, loading: authLoading, signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    const destination =
      platformRole === "admin" || platformRole === "staff"
        ? "/admin"
        : "/dashboard";
    nav(destination, { replace: true });
  }, [authLoading, user, platformRole, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      toast.success("Signed in.");
    } catch (err) {
      toast.error(err?.message || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav />
      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="fx-mono-tag mb-4">§ CLIENT PORTAL</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Welcome back.</h1>
          <p className="mt-5 text-[#475569] max-w-sm">Log in to review projects, chat with the Agenaix Assistant, and submit new briefs.</p>
          <div className="mt-8 border-t border-[#DCE3ED] pt-5 fx-mono-tag">
            NEED ACCESS?{" "}
            <Link to="/signup" className="text-[#0B4DCC] hover:underline" data-testid="link-to-signup">VIEW ACCESS STATUS →</Link>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-7 md:col-start-7 border border-[#DCE3ED] p-8 lg:p-10 bg-white rounded-xl" data-testid="login-form">
          <div className="fx-mono-tag mb-6">LOGIN CREDENTIALS</div>
          <div className="space-y-5">
            <div>
              <Label className="fx-mono-tag">EMAIL</Label>
              <Input required type="email" autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} className="mt-2 h-11 rounded-md" placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="fx-mono-tag">PASSWORD</Label>
                <Link to="/forgot-password" className="text-xs font-mono uppercase tracking-widest text-[#0B4DCC] hover:underline" data-testid="link-forgot-password">FORGOT?</Link>
              </div>
              <Input required type="password" autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} className="mt-2 h-11 rounded-md" placeholder="••••••••" data-testid="login-password-input" />
            </div>
          </div>
          <Button disabled={loading} className="fx-btn-primary h-11 w-full rounded-lg mt-6 text-sm font-semibold" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign in →"}
          </Button>
        </form>
      </div>
    </div>
  );
}
